import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import { Card } from '@mui/material';
import { FixedCenterCircularProgress } from 'src/components/progress/fixed-center-circular-progress';
import { DashboardContent } from 'src/layouts/dashboard';
import type { TableHeadCellProps } from 'src/components/table';
import { TableNoData, TableHeadCustom } from 'src/components/table';
import { useSearchParams } from 'react-router';
import { useSetState } from 'minimal-shared/hooks';
import { useEffect } from 'react';
import { buildURLQueryParams } from 'src/utils/helpers';
import { TableLoading } from 'src/components/table/table-loading';
import { CustomServerPagination } from 'src/components/custom-server-pagination/CustomServerPagination';

type Props = {
  isLoading: boolean;
  isFetching: boolean;
  tableHeadColumns: TableHeadCellProps[];
  initialFiltersValue: Record<string, any>;
  updateFiltersCallback: () => Props['initialFiltersValue'];
  data: Array<any>;
  customGridRow: (rowProps: any) => JSX.Element;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  sorting: Record<string, 'asc' | 'desc'>;
  onSort: (id: string) => void;
};

export function ReusableServerTable({
  isLoading,
  isFetching,
  tableHeadColumns,
  initialFiltersValue,
  data,
  updateFiltersCallback,
  customGridRow,
  currentPage,
  totalPages,
  onPageChange,
  sorting,
  onSort,
}: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useSetState<any>(initialFiltersValue);
  const { state: currentFilters, setState: updateFilters } = filters;

  useEffect(() => {
    updateFilters(updateFiltersCallback);
  }, [updateFilters, updateFiltersCallback]);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        return {
          ...buildURLQueryParams(
            {
              ...currentFilters,
              type: searchParams.get('type'),
            },
            prev
          ),
        };
      },
      { replace: true }
    );
  }, [currentFilters, setSearchParams, searchParams]);

  const notFound = !data.length;

  return (
    <DashboardContent>
      {isLoading && <FixedCenterCircularProgress />}

      {!isLoading && (
        <Card>
          <Box sx={{ position: 'relative', overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 1000 }}>
              <TableHeadCustom
                order={Object.values(sorting)[0] || 'asc'}
                orderBy={Object.keys(sorting)[0] || ''}
                headCells={tableHeadColumns}
                rowCount={data.length}
                numSelected={0}
                onSort={onSort}
              />

              <TableBody>
                {!isFetching && (
                  <>
                    {data.map((row, index) =>
                      customGridRow({
                        rowIndex: index,
                        row,
                        table: { selected: [] },
                      })
                    )}

                    <TableNoData notFound={notFound} />
                  </>
                )}

                {isFetching && <TableLoading />}
              </TableBody>
            </Table>
          </Box>

          {!isFetching && (
            <CustomServerPagination
              currentPage={currentPage}
              totalPages={totalPages}
              changePage={onPageChange}
            />
          )}
        </Card>
      )}
    </DashboardContent>
  );
}
